import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import WeatherWidget from './WeatherWidget';

const CONDITION_EMOJI = {
  Clear: '☀️', Clouds: '⛅', Rain: '🌧️', Thunderstorm: '⛈️',
  Drizzle: '🌦️', Snow: '❄️', Mist: '🌫️', Fog: '🌫️', Haze: '🌫️',
};
function condIcon(c) { return CONDITION_EMOJI[c] || '🌤️'; }

function multiplierColor(m) {
  if (m == null) return '#94a3b8';
  if (m >= 1.0) return '#16a34a';
  if (m >= 0.85) return '#d97706';
  return '#dc2626';
}

/* ── WMO weather code → emoji ─────────────────────────────────── */
const WMO_EMOJI = {
  0: '☀️', 1: '🌤️', 2: '⛅', 3: '☁️',
  45: '🌫️', 48: '🌫️',
  51: '🌦️', 53: '🌦️', 55: '🌧️',
  61: '🌦️', 63: '🌧️', 65: '⛈️',
  71: '🌨️', 73: '❄️', 75: '❄️',
  80: '🌧️', 81: '🌧️', 82: '⛈️',
  95: '⛈️', 96: '⛈️', 99: '⛈️',
};
function wmoEmoji(code) { return WMO_EMOJI[code] || '🌡️'; }

/* ── Meal period order + colours (shared) ─────────────────────── */
const PERIOD_ORDER = ['Breakfast', 'Lunch', 'Afternoon', 'Dinner', 'Late Night', 'All Day'];
const PERIOD_COLOR = {
  Breakfast: '#f59e0b', Lunch: '#10b981', Afternoon: '#3b82f6',
  Dinner: '#8b5cf6', 'Late Night': '#ec4899', 'All Day': '#0d9488',
};
const PERIOD_TIME  = {
  Breakfast: 'before 11am', Lunch: '11am–3pm', Afternoon: '3pm–5pm',
  Dinner: '5pm–10pm', 'Late Night': '10pm+',
};

/* ── Sparkline SVG for a single day ───────────────────────────── */
function DaySparkline({ preds }) {
  const ordered = PERIOD_ORDER
    .map(p => preds.find(r => r.meal_period === p))
    .filter(Boolean)
    .filter(r => r.meal_period !== 'All Day');
  if (ordered.length < 2) return null;

  const W = 120, H = 36, pad = 4;
  const vals = ordered.map(r => r.avg_covers || 0);
  const max  = Math.max(...vals, 1);
  const pts  = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v / max) * (H - pad * 2));
    return [x, y];
  });
  const pathD = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaD = pathD + ` L${pts[pts.length-1][0].toFixed(1)},${H} L${pts[0][0].toFixed(1)},${H} Z`;

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', margin: '4px auto 0' }}>
      <defs>
        <linearGradient id="spkGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d9488" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#0d9488" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={areaD} fill="url(#spkGrad)" />
      <path d={pathD} fill="none" stroke="#0d9488" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="2" fill="#0d9488" />
      ))}
    </svg>
  );
}

/* ── Day detail panel shown below the strip ───────────────────── */
function DayDetail({ day }) {
  if (!day) return null;
  const w      = day.weather || {};
  const preds  = (day.predictions || [])
    .filter(r => r.meal_period !== 'All Day')
    .sort((a, b) => PERIOD_ORDER.indexOf(a.meal_period) - PERIOD_ORDER.indexOf(b.meal_period));
  const allDay = day.predictions?.find(r => r.meal_period === 'All Day');
  const items  = preds.length ? preds : (allDay ? [allDay] : []);
  if (!items.length) return null;

  const maxCovers = Math.max(...items.map(r => r.avg_covers || 0), 1);
  const label = new Date(day.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'long', month: 'short', day: 'numeric' });

  return (
    <div style={{ marginTop: 12, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: day.holiday_name ? 8 : 12 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>
          {wmoEmoji(w.weather_code)} {label}
          {w.temp_high_f != null && <span style={{ fontWeight: 400, fontSize: 12, color: '#64748b', marginLeft: 8 }}>{Math.round(w.temp_high_f)}° high</span>}
        </div>
        {day.vs_average && (
          <span style={{ fontSize: 12, fontWeight: 700, color: day.vs_average === 'above' ? '#16a34a' : day.vs_average === 'below' ? '#dc2626' : '#64748b' }}>
            {day.vs_average === 'above'
              ? `▲ ${day.vs_average_pct}% vs ${day.holiday_name ? day.holiday_name + ' avg' : `typical ${day.dow_name || 'day'}`}`
              : day.vs_average === 'below'
              ? `▼ ${Math.abs(day.vs_average_pct)}% vs ${day.holiday_name ? day.holiday_name + ' avg' : `typical ${day.dow_name || 'day'}`}`
              : `≈ ${day.holiday_name ? day.holiday_name + ' avg' : `typical ${day.dow_name || 'day'}`}`}
          </span>
        )}
      </div>

      {/* Holiday banner */}
      {day.holiday_name && (
        <div style={{ marginBottom: 12, padding: '7px 10px', borderRadius: 7, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>🎉</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#d97706' }}>{day.holiday_name}</div>
            {day.holiday_avg != null && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>
                Your historical avg: <strong style={{ color: '#d97706' }}>{day.holiday_avg} covers</strong>
                {day.holiday_years != null && <span style={{ marginLeft: 4 }}>({day.holiday_years} yr{day.holiday_years !== 1 ? 's' : ''} of data)</span>}
              </div>
            )}
            {day.holiday_avg == null && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>First occurrence in your data — no historical comparison yet</div>
            )}
          </div>
        </div>
      )}
      {items.map(r => {
        const pct   = Math.round((r.avg_covers / maxCovers) * 100);
        const color = PERIOD_COLOR[r.meal_period] || '#0d9488';
        return (
          <div key={r.meal_period} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <div>
                <span style={{ fontSize: 12, fontWeight: 700 }}>{r.meal_period}</span>
                {PERIOD_TIME[r.meal_period] && (
                  <span style={{ fontSize: 10, color: '#64748b', marginLeft: 6 }}>{PERIOD_TIME[r.meal_period]}</span>
                )}
              </div>
              <span style={{ fontSize: 13, fontWeight: 800, color }}>👥 {r.avg_covers}</span>
            </div>
            <div style={{ height: 6, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .4s ease' }} />
            </div>
          </div>
        );
      })}
      {/* Why is it above/below average? */}
      {day.reason && (
        <div style={{ marginTop: 10, fontSize: 12, color: '#94a3b8', lineHeight: 1.6, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, borderLeft: '2px solid rgba(13,148,136,0.3)' }}>
          <div style={{ fontStyle: 'italic' }}>💡 {day.reason}</div>
          {(day.holiday_avg != null || day.dow_avg != null) && (
            <div style={{ marginTop: 5, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {day.holiday_avg != null ? (
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  {day.holiday_name} avg: <strong style={{ color: '#d97706' }}>{day.holiday_avg} covers</strong>
                </span>
              ) : (
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  Typical {day.dow_name}: <strong style={{ color: '#94a3b8' }}>{day.dow_avg} covers</strong>
                </span>
              )}
              {day.weather_impact_pct != null && Math.abs(day.weather_impact_pct) >= 5 && (
                <span style={{ fontSize: 11, color: '#64748b' }}>
                  Weather impact:{' '}
                  <strong style={{ color: day.weather_impact_pct > 0 ? '#16a34a' : '#dc2626' }}>
                    {day.weather_impact_pct > 0 ? '+' : ''}{day.weather_impact_pct}%
                  </strong>
                </span>
              )}
            </div>
          )}
        </div>
      )}
      {day.inventory_alert?.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 12, color: '#f59e0b', display: 'flex', gap: 6, alignItems: 'center' }}>
          ⚠️ High-traffic day — low stock: {day.inventory_alert.map(i => i.name).join(', ')}
        </div>
      )}
    </div>
  );
}

/* ── Covers forecast strip ────────────────────────────────────── */
function CoversForecast() {
  const [days, setDays]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selected, setSelected]   = useState(0);

  useEffect(() => {
    api.get('/covers/predictions')
      .then(({ data }) => {
        const predictions = data.predictions || [];
        if (!predictions.length) {
          setError(data.error === 'weather_unavailable'
            ? 'Weather API unavailable — try again shortly'
            : 'No covers data yet');
          return;
        }
        setDays(predictions);
      })
      .catch(err => setError('Could not load forecast (' + (err.response?.status || 'network error') + ')'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#64748b', fontSize: 13, padding: '12px 0' }}>Loading forecast…</div>;
  if (error || !days.length) return (
    <div style={{ color: '#64748b', fontSize: 13, padding: '8px 0' }}>
      {error === 'No covers data yet'
        ? <>No covers forecast yet — <a href="/admin/covers-import" style={{ color: '#0d9488' }}>import your Toast data</a> to enable predictions</>
        : error || 'No forecast available'}
    </div>
  );

  return (
    <div>
      {/* 7-day strip */}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(days.length, 7)}, 1fr)`, gap: 8 }}>
        {days.map((day, i) => {
          const w        = day.weather || {};
          const preds    = day.predictions || [];
          const covers   = preds.reduce((s, p) => s + (p.avg_covers || 0), 0) || null;
          const vs       = day.vs_average;
          const vsPct    = day.vs_average_pct;
          const vsColor  = vs === 'above' ? '#16a34a' : vs === 'below' ? '#dc2626' : '#64748b';
          const hasAlert = day.inventory_alert?.length > 0;
          const isSelected = selected === i;
          const isHoliday = !!day.holiday_name;

          return (
            <div
              key={i}
              onClick={() => setSelected(i)}
              style={{
                background: isSelected ? 'rgba(13,148,136,0.08)' : isHoliday ? 'rgba(245,158,11,0.05)' : 'rgba(255,255,255,0.04)',
                borderRadius: 10, padding: '10px 6px', textAlign: 'center',
                cursor: 'pointer',
                border: `1px solid ${isSelected ? 'rgba(13,148,136,.5)' : isHoliday ? 'rgba(245,158,11,.35)' : hasAlert ? 'rgba(245,158,11,.4)' : 'rgba(255,255,255,.08)'}`,
                position: 'relative', transition: 'all .15s',
              }}
            >
              {hasAlert && !isHoliday && (
                <div style={{ position: 'absolute', top: 4, right: 4, fontSize: 10 }} title={`Low stock: ${day.inventory_alert.map(it => it.name).join(', ')}`}>⚠️</div>
              )}
              {isHoliday && (
                <div style={{ position: 'absolute', top: 4, right: 4, fontSize: 10 }} title={day.holiday_name}>🎉</div>
              )}
              <div style={{ fontSize: 10, fontWeight: 700, color: isSelected ? '#0d9488' : isHoliday ? '#d97706' : '#64748b', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 3 }}>
                {i === 0 ? 'Today' : new Date(day.date + 'T12:00:00').toLocaleDateString('en', { weekday: 'short' })}
              </div>
              {isHoliday && (
                <div style={{ fontSize: 8, fontWeight: 700, color: '#d97706', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', paddingLeft: 2, paddingRight: 14 }}>
                  {day.holiday_name}
                </div>
              )}
              <div style={{ fontSize: 20, marginBottom: 2 }}>{wmoEmoji(w.weather_code)}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 3, marginBottom: 4 }}>
                {w.temp_high_f != null && <span style={{ fontSize: 12, fontWeight: 700 }}>{Math.round(w.temp_high_f)}°</span>}
                {w.temp_low_f  != null && <span style={{ fontSize: 10, color: '#64748b' }}>{Math.round(w.temp_low_f)}°</span>}
              </div>
              {covers != null && (
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0d9488', marginBottom: 1 }}>
                  👥 {covers}
                </div>
              )}
              {vs && (
                <div style={{ fontSize: 10, fontWeight: 700, color: vsColor }}>
                  {vs === 'above' ? `▲ ${vsPct}%` : vs === 'below' ? `▼ ${Math.abs(vsPct)}%` : '≈ avg'}
                </div>
              )}
              {w.precipitation_mm > 0 && (
                <div style={{ fontSize: 9, color: '#60a5fa', marginTop: 2 }}>🌧 {w.precipitation_mm}mm</div>
              )}
              {/* Mini sparkline */}
              <DaySparkline preds={preds} />
            </div>
          );
        })}
      </div>

      {/* Detail panel for selected day */}
      <DayDetail day={days[selected]} />
    </div>
  );
}

/* ── Below-par items ──────────────────────────────────────────── */
function BelowParList({ navigate, basePath }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/inventory?below_par=true')
      .then(({ data }) => setItems(Array.isArray(data) ? data.slice(0, 8) : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>;
  if (!items.length) return (
    <div style={{ color: '#16a34a', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, padding: '8px 0' }}>
      ✓ All items above par
    </div>
  );

  return (
    <div>
      {items.map(item => {
        const pct = item.par_level > 0 ? Math.round((item.quantity_on_hand / item.par_level) * 100) : 0;
        return (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>{item.quantity_on_hand} / {item.par_level} {item.unit}</div>
            </div>
            <div style={{ flexShrink: 0 }}>
              <div style={{ width: 48, height: 6, background: 'rgba(255,255,255,.08)', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct < 25 ? '#ef4444' : '#f59e0b', borderRadius: 3 }} />
              </div>
              <div style={{ fontSize: 10, color: pct < 25 ? '#ef4444' : '#f59e0b', textAlign: 'right', marginTop: 2 }}>{pct}%</div>
            </div>
          </div>
        );
      })}
      {items.length >= 8 && (
        <button onClick={() => navigate(`${basePath}/inventory`)} style={{ fontSize: 12, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer', padding: '8px 0 0' }}>
          View all →
        </button>
      )}
    </div>
  );
}

/* ── Recent invoice activity ──────────────────────────────────── */
function RecentInvoices({ navigate, basePath }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/invoices?limit=5')
      .then(({ data }) => setInvoices(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>;
  if (!invoices.length) return <div style={{ color: '#64748b', fontSize: 13 }}>No invoices yet</div>;

  return (
    <div>
      {invoices.map(inv => (
        <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{inv.vendor_name || 'Unknown vendor'}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>{new Date(inv.scanned_at).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</div>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '2px 7px',
            background: inv.status === 'complete' ? 'rgba(22,163,74,.15)' : 'rgba(245,158,11,.15)',
            color: inv.status === 'complete' ? '#16a34a' : '#d97706',
          }}>
            {inv.status}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Reorder suggestions ──────────────────────────────────────── */
function ReorderSuggestions() {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/weather/reorder-suggestions')
      .then(({ data }) => setSuggestions(Array.isArray(data) ? data.slice(0, 5) : (data.suggestions || []).slice(0, 5)))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ color: '#64748b', fontSize: 13 }}>Loading…</div>;
  if (!suggestions.length) return <div style={{ color: '#64748b', fontSize: 13 }}>No adjustments needed</div>;

  return (
    <div>
      {suggestions.map((s, i) => (
        <div key={s.id || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.item_name || s.name}</div>
            {s.weather_note && <div style={{ fontSize: 11, color: '#64748b' }}>{s.weather_note}</div>}
          </div>
          <div style={{ flexShrink: 0, textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0d9488' }}>+{s.suggested_order ?? s.suggested_quantity} {s.unit}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Main dashboard ───────────────────────────────────────────── */
export default function OpsDashboard({ basePath = '/admin' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pendingOrders, setPendingOrders] = useState(null);

  useEffect(() => {
    api.get('/orders?status=draft')
      .then(({ data }) => setPendingOrders(Array.isArray(data) ? data.length : 0))
      .catch(() => setPendingOrders(0));
  }, []);

  const today = new Date().toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric' });

  return (
    <>
      <div className="page-header">
        <div>
          <span className="page-title">Dashboard</span>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{today} · {user?.restaurant_name || user?.name}</div>
        </div>
        {pendingOrders > 0 && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`${basePath}/orders`)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {pendingOrders} order{pendingOrders !== 1 ? 's' : ''} need approval
          </button>
        )}
      </div>

      <div className="page-body">
        {/* Top row: weather now + 5-day covers forecast */}
        <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, marginBottom: 16 }}>
          <WeatherWidget />
          <div className="card">
            <div className="card-header">📅 Covers Forecast</div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              <CoversForecast />
            </div>
          </div>
        </div>

        {/* Middle row: below par + reorder suggestions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>⚠️ Below Par</span>
              <button onClick={() => navigate(`${basePath}/inventory`)} style={{ fontSize: 11, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}>All inventory →</button>
            </div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              <BelowParList navigate={navigate} basePath={basePath} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">🌦️ Weather-Adjusted Reorder</div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              <ReorderSuggestions />
            </div>
          </div>
        </div>

        {/* Bottom row: recent invoices + quick actions */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>🧾 Recent Invoices</span>
              {basePath === '/admin' && (
                <button onClick={() => navigate(`${basePath}/invoices`)} style={{ fontSize: 11, color: '#0d9488', background: 'none', border: 'none', cursor: 'pointer' }}>History →</button>
              )}
            </div>
            <div className="card-body" style={{ paddingTop: 8 }}>
              <RecentInvoices navigate={navigate} basePath={basePath} />
            </div>
          </div>

          <div className="card">
            <div className="card-header">Quick Actions</div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
              <button className="btn btn-primary" onClick={() => navigate(`${basePath}/scan`)} style={{ width: '100%', justifyContent: 'center' }}>
                📷 Scan Invoice
              </button>
              <button className="btn" onClick={() => navigate(`${basePath}/inventory`)} style={{ width: '100%', justifyContent: 'center', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)' }}>
                📦 Inventory
              </button>
              {pendingOrders > 0 && (
                <button className="btn" onClick={() => navigate(`${basePath}/orders`)} style={{ width: '100%', justifyContent: 'center', background: 'rgba(245,158,11,.12)', border: '1px solid rgba(245,158,11,.3)', color: '#f59e0b' }}>
                  ✅ {pendingOrders} Pending Approval{pendingOrders !== 1 ? 's' : ''}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
